import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Formik } from 'formik';

import { config } from '@xingternal/hops';
import TextInput from '@xingternal/malt-text-input';
import {
  PrimaryButtonMedium as Button,
  TextButtonRegularSmall as TextButton,
} from '@xingternal/malt-button';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';

import Store from './../../../store';

import PageviewTracker from '../../../Components/PageviewTracker/PageviewTracker';
import IllustrationPlaceholder from './../../../Components/Illustrations/IllustrationPlaceholder';
import StretchButtonGroup from './../../../Components/StretchButtonGroup/StretchButtonGroup';
import FormMessage from './../../../Components/FormMessage/FormMessage';
import Spacer from './../../../Components/Spacer/Spacer';
import { CustomH, CustomP } from './../../../Components/CustomMalt/Typography';

import parseQueryString from './../../../utils/parseQueryString';
import clearUrlParams from './../../../utils/clearUrlParams';
import api from './../../../utils/api';

import messages from './messages';
import styles from './RecoveryEntry.css';

const TestableForm = testable(QA_MALT, 'form');

const maybeTokenError = routerLocation => {
  const query =
    routerLocation &&
    routerLocation.search &&
    parseQueryString(routerLocation.search);

  clearUrlParams();

  if (query && query.error === 'LOGIN_REQUIRED') {
    return <FormattedMessage {...messages.invalidCode} />;
  }

  if (query && query.error === 'INVALID_LINK') {
    return <FormattedMessage {...messages.invalidLink} />;
  }

  return false;
};

const RecoveryEntry = ({ location, history: { push } }) => {
  const { dispatch, state } = useContext(Store);
  const tokenError = maybeTokenError(location);

  return (
    <>
      <PageviewTracker
        pageName={'wbm/Welcome/login/recovery'}
        contextData={
          tokenError && {
            PropContextDimension1: 'welcome_login_page_recovery_password_error',
          }
        }
      />

      {/* temporary until we decide what to do with illustrations */}
      <IllustrationPlaceholder />

      <CustomH>
        <FormattedMessage {...messages.title} />
      </CustomH>

      <CustomP>
        <FormattedMessage {...messages.description} />
      </CustomP>

      <Formik
        initialValues={{
          username: state.username || '',
        }}
        onSubmit={({ username }, { setErrors, setSubmitting }) => {
          api
            .postRetry('/recovery/password/reset_requests', { username })
            .then(() => {
              dispatch({ type: 'SET_USERNAME', payload: { username } });
              push('/recovery/password/confirm');
            })
            .catch(err => {
              setErrors({ username: err.jsonResponse.error });
              setSubmitting(false);
            });
        }}
        render={({
          handleSubmit,
          isSubmitting,
          handleChange,
          errors,
          values: { username },
        }) => (
          <TestableForm
            testId={'recovery-entry-form'}
            onSubmit={handleSubmit}
            method={'POST'}
          >
            <TextInput
              name={'username'}
              onChange={handleChange}
              label={<FormattedMessage {...messages.inputLabel} />}
              value={username}
              type={'text'}
              size={'m'}
              message={errors && errors.username}
              required
              autoFocus
            />

            <FormMessage error={tokenError} />

            <StretchButtonGroup>
              <Button
                loading={isSubmitting}
                type={'submit'}
                testId={'recovery-next-button'}
              >
                <FormattedMessage {...messages.buttonContinue} />
              </Button>

              <TextButton
                to={'/recovery/account/no-access'}
                noPadding
                testId={'recovery-no-access-button'}
              >
                <FormattedMessage
                  {...messages.forgotOrNoAccessCredentialsRecoveryLabel}
                />
              </TextButton>
            </StretchButtonGroup>

            <Spacer size={8} />

            <div className={styles.center}>
              <CustomP noMargin>
                <FormattedMessage
                  {...messages.problemsLoginRecoveryIntroduction}
                />
              </CustomP>
              <TextButton href={`${config.externalUrl}/support/contact/login`}>
                <FormattedMessage {...messages.problemsLoginRecoveryLabel} />
              </TextButton>
            </div>
          </TestableForm>
        )}
      />
    </>
  );
};

export default RecoveryEntry;
